import {useMemo} from "react";
import {css} from "@panda/css";
import type {AxisLabelsFormatterContextObject, Chart as HighchartsChart, Options, Point, PointLabelObject, SVGElement} from "highcharts";

import {CenterBox} from "@pg-design/grid-module";
import {Loader} from "@pg-design/loader-module";
import {Chart, getDefaultXAxisOptions, getDefaultYAxisOptions} from "@pg-mono/charts";
import {numberFormat, pluralize, priceFormat, priceM2Format} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {formatRcnPointPeriod} from "../../utils/format_rcn_period_label";

export type PricesAddressComparisonBarKind = "okolica" | "dzielnica" | "miasto";

export interface IPricesAddressComparisonBar {
    kind: PricesAddressComparisonBarKind;
    label: string;
    sublabel: string;
    color: string;
    value: number;
    nRows: number;
    periodStart: string;
    /** RCN period_label of the point - "2026-06", "2025-Q2" or "2025". */
    periodLabel: string;
}

interface Props {
    bars: IPricesAddressComparisonBar[];
    isLoading: boolean;
}

interface IBarPointCustom {
    kind: PricesAddressComparisonBarKind;
    label: string;
    nRows: number;
    periodStart: string;
    /** RCN period_label of the point - "2026-06", "2025-Q2" or "2025". */
    periodLabel: string;
}

const DESKTOP_CHART_HEIGHT = "320px";
const MOBILE_CHART_HEIGHT = "260px";
const AXIS_STEP = 250;
// Room above the plot area for the two-line value label that sits on top of the tallest bar
const CHART_SPACING_TOP = 52;

interface ChartWithDifferenceMarks extends HighchartsChart {
    differenceMarks?: SVGElement;
}

const DIFFERENCE_MARK_OFFSET = 14;
const DIFFERENCE_MARK_CAP_WIDTH = 11;
const DIFFERENCE_MARK_LABEL_OFFSET = 10;
const DIFFERENCE_MARK_LABEL_MIN_OFFSET = 18;
const DIFFERENCE_MARK_STROKE = 1.5;

export const PricesAddressComparisonChart = (props: Props) => {
    const {bars, isLoading} = props;
    const {isMobile} = useUserDevice();

    const chartHeight = isMobile ? MOBILE_CHART_HEIGHT : DESKTOP_CHART_HEIGHT;
    const showDifferenceMarks = !isMobile;

    const options = useMemo<Options>(() => {
        const values = bars.map((bar) => Math.round(bar.value));
        const baseValue = values[bars.findIndex((bar) => bar.kind === "okolica")] ?? null;
        const hasValues = values.length > 0;

        return {
            chart: {
                type: "column",
                height: chartHeight,
                spacingTop: CHART_SPACING_TOP,
                events: {
                    render: function (this: ChartWithDifferenceMarks) {
                        drawDifferenceMarks(this, baseValue, showDifferenceMarks);
                    }
                }
            },
            title: {text: ""},
            legend: {enabled: false},
            xAxis: {
                ...getDefaultXAxisOptions(),
                categories: bars.map((bar) => bar.label),
                labels: {
                    enabled: true,
                    useHTML: true,
                    formatter: function (this: AxisLabelsFormatterContextObject) {
                        const bar = bars[this.pos];

                        if (!bar) {
                            return String(this.value);
                        }

                        return `<div style="text-align:center;line-height:1.35"><span style="font-size:14px;color:var(--colors-secondary)">${bar.label}</span><br/><span style="font-size:11px;color:var(--colors-gray-600)">${bar.sublabel}</span></div>`;
                    }
                }
            },
            yAxis: {
                ...getDefaultYAxisOptions(),
                gridLineColor: "var(--colors-gray-200)",
                min: hasValues ? Math.floor((Math.min(...values) * 0.97) / AXIS_STEP) * AXIS_STEP : undefined,
                max: hasValues ? Math.ceil((Math.max(...values) * 1.03) / AXIS_STEP) * AXIS_STEP : undefined,
                tickAmount: 6,
                labels: {
                    enabled: true,
                    style: {color: "var(--colors-gray-700)", fontSize: "1.106rem"},
                    formatter: ({value}: {value: string | number}) => numberFormat(Number(value), {precision: 0}) ?? String(value)
                },
                plotLines:
                    baseValue === null
                        ? []
                        : [
                              {
                                  value: baseValue,
                                  color: "var(--colors-secondary)",
                                  dashStyle: "Dash" as const,
                                  width: 1.5,
                                  zIndex: 4
                              }
                          ]
            },
            plotOptions: {
                column: {
                    borderRadius: 8,
                    borderWidth: 0,
                    maxPointWidth: 132,
                    pointPadding: 0.2,
                    groupPadding: 0.15,
                    dataLabels: {
                        enabled: true,
                        useHTML: true,
                        inside: false,
                        crop: false,
                        overflow: "allow" as const,
                        style: {fontSize: "1.6rem", fontWeight: "500", color: "var(--colors-secondary)", textOutline: "none"},
                        formatter: function (this: PointLabelObject) {
                            const value = this.y ?? null;

                            return value === null ? "" : `<div style="text-align:center;line-height:1.35"><b>${priceM2Format(value, {precision: 0})}</b></div>`;
                        }
                    }
                }
            },
            series: [
                {
                    type: "column" as const,
                    name: "Mediana ceny za m²",
                    data: bars.map((bar) => ({
                        y: Math.round(bar.value),
                        color: bar.color,
                        custom: {
                            kind: bar.kind,
                            label: bar.label,
                            nRows: bar.nRows,
                            periodStart: bar.periodStart,
                            periodLabel: bar.periodLabel
                        } satisfies IBarPointCustom
                    }))
                }
            ],
            tooltip: {
                // The value labels above the bars are HTML, so they live in an overlay above the
                // chart's SVG - the tooltip has to leave that container to stay on top of them.
                outside: true,
                borderColor: "transparent",
                backgroundColor: "var(--colors-white)",
                shadow: {color: "rgba(35, 35, 45, 0.24)", offsetX: 0, offsetY: 2, opacity: 0.5, width: 6},
                style: {color: "var(--colors-secondary)", fontSize: "1.2rem", zIndex: 9},
                headerFormat: "",
                pointFormatter: function (this: Point) {
                    const custom = getPointCustom(this);
                    const value = this.y ?? 0;
                    const priceLabel = `<b>${priceM2Format(value, {precision: 0})}</b>`;

                    if (!custom) {
                        return priceLabel;
                    }

                    return `${custom.label}: ${priceLabel}<br/>${custom.nRows} ${transactionsPluralize(custom.nRows)} · ${formatRcnPointPeriod({period_start: custom.periodStart, period_label: custom.periodLabel})}`;
                }
            }
        };
    }, [bars, chartHeight, showDifferenceMarks]);

    return (
        <div className={chartHolderStyle}>
            <Chart
                height={chartHeight}
                options={options}
                loader={
                    isLoading ? (
                        <CenterBox height={chartHeight}>
                            <Loader size="lg" />
                        </CenterBox>
                    ) : null
                }
            />
        </div>
    );
};

const transactionsPluralize = pluralize(["transakcja", "transakcje", "transakcji"]);

const getPointCustom = (point: Point): IBarPointCustom | undefined => point.options.custom as IBarPointCustom | undefined;

const getDifferenceLabel = (difference: number): string => `${difference > 0 ? "+" : "−"}${priceFormat(Math.abs(difference), {precision: 0})}`.toUpperCase();

const drawDifferenceMarks = (chart: ChartWithDifferenceMarks, baseValue: number | null, isEnabled: boolean) => {
    chart.differenceMarks?.destroy();

    chart.differenceMarks = undefined;

    const series = chart.series[0];

    if (!isEnabled || baseValue === null || !series) {
        return;
    }

    const group = chart.renderer.g("prices-address-difference").attr({zIndex: 6}).add();
    const baseY = series.yAxis.toPixels(baseValue, false);

    for (const point of series.points) {
        const custom = getPointCustom(point);
        const shape = point.shapeArgs;

        if (!custom || custom.kind === "okolica" || point.y === undefined || shape?.x === undefined || shape.width === undefined) {
            continue;
        }

        const difference = Math.round(point.y) - baseValue;

        if (difference === 0) {
            continue;
        }

        const x = chart.plotLeft + shape.x + shape.width + DIFFERENCE_MARK_OFFSET;
        const barY = series.yAxis.toPixels(point.y, false);
        const cap = DIFFERENCE_MARK_CAP_WIDTH / 2;

        chart.renderer
            .path([
                ["M", x, barY],
                ["L", x, baseY],
                ["M", x - cap, barY],
                ["L", x + cap, barY],
                ["M", x - cap, baseY],
                ["L", x + cap, baseY]
            ])
            .attr({"stroke-width": DIFFERENCE_MARK_STROKE})
            .css({stroke: "var(--colors-secondary)"})
            .add(group);

        // Centred on the mark, but never so high that it lands on the bar's own price label
        const labelY = Math.max((barY + baseY) / 2 + 4, Math.min(barY, baseY) + DIFFERENCE_MARK_LABEL_MIN_OFFSET);

        chart.renderer
            .text(getDifferenceLabel(difference), x + DIFFERENCE_MARK_LABEL_OFFSET, labelY)
            .css({color: "var(--colors-secondary)", fontSize: "1.24rem", fontWeight: "600", letterSpacing: "0.248px"})
            .add(group);
    }

    chart.differenceMarks = group;
};

const chartHolderStyle = css({
    width: "100%"
});
import {useMemo} from "react";
import {css} from "@panda/css";
import type {AxisLabelsFormatterContextObject, Chart as HighchartsChart, Options, Point, PointLabelObject, SVGElement} from "highcharts";

import {CenterBox} from "@pg-design/grid-module";
import {Loader} from "@pg-design/loader-module";
import {Chart, getDefaultXAxisOptions, getDefaultYAxisOptions} from "@pg-mono/charts";
import {numberFormat, pluralize, priceFormat, priceM2Format} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {formatRcnPointPeriod} from "../../utils/format_rcn_period_label";

export type PricesAddressComparisonBarKind = "okolica" | "dzielnica" | "miasto";

export interface IPricesAddressComparisonBar {
    kind: PricesAddressComparisonBarKind;
    label: string;
    sublabel: string;
    color: string;
    value: number;
    nRows: number;
    periodStart: string;
    /** RCN period_label of the point - "2026-06", "2025-Q2" or "2025". */
    periodLabel: string;
}

interface Props {
    bars: IPricesAddressComparisonBar[];
    isLoading: boolean;
}

interface IBarPointCustom {
    kind: PricesAddressComparisonBarKind;
    label: string;
    nRows: number;
    periodStart: string;
    /** RCN period_label of the point - "2026-06", "2025-Q2" or "2025". */
    periodLabel: string;
}

const DESKTOP_CHART_HEIGHT = "320px";
const MOBILE_CHART_HEIGHT = "260px";
const AXIS_STEP = 250;
// Room above the plot area for the two-line value label that sits on top of the tallest bar
const CHART_SPACING_TOP = 52;

interface ChartWithDifferenceMarks extends HighchartsChart {
    differenceMarks?: SVGElement;
}

const DIFFERENCE_MARK_OFFSET = 14;
const DIFFERENCE_MARK_CAP_WIDTH = 11;
const DIFFERENCE_MARK_LABEL_OFFSET = 10;
const DIFFERENCE_MARK_LABEL_MIN_OFFSET = 18;
const DIFFERENCE_MARK_STROKE = 1.5;

export const PricesAddressComparisonChart = (props: Props) => {
    const {bars, isLoading} = props;
    const {isMobile} = useUserDevice();

    const chartHeight = isMobile ? MOBILE_CHART_HEIGHT : DESKTOP_CHART_HEIGHT;
    const showDifferenceMarks = !isMobile;

    const options = useMemo<Options>(() => {
        const values = bars.map((bar) => Math.round(bar.value));
        const baseValue = values[bars.findIndex((bar) => bar.kind === "okolica")] ?? null;
        const hasValues = values.length > 0;

        return {
            chart: {
                type: "column",
                height: chartHeight,
                spacingTop: CHART_SPACING_TOP,
                events: {
                    render: function (this: ChartWithDifferenceMarks) {
                        drawDifferenceMarks(this, baseValue, showDifferenceMarks);
                    }
                }
            },
            title: {text: ""},
            legend: {enabled: false},
            xAxis: {
                ...getDefaultXAxisOptions(),
                categories: bars.map((bar) => bar.label),
                labels: {
                    enabled: true,
                    useHTML: true,
                    formatter: function (this: AxisLabelsFormatterContextObject) {
                        const bar = bars[this.pos];

                        if (!bar) {
                            return String(this.value);
                        }

                        return `<div style="text-align:center;line-height:1.35"><span style="font-size:14px;color:var(--colors-secondary)">${bar.label}</span><br/><span style="font-size:11px;color:var(--colors-gray-600)">${bar.sublabel}</span></div>`;
                    }
                }
            },
            yAxis: {
                ...getDefaultYAxisOptions(),
                gridLineColor: "var(--colors-gray-200)",
                min: hasValues ? Math.floor((Math.min(...values) * 0.97) / AXIS_STEP) * AXIS_STEP : undefined,
                max: hasValues ? Math.ceil((Math.max(...values) * 1.03) / AXIS_STEP) * AXIS_STEP : undefined,
                tickAmount: 6,
                labels: {
                    enabled: true,
                    style: {color: "var(--colors-gray-700)", fontSize: "1.106rem"},
                    formatter: ({value}: {value: string | number}) => numberFormat(Number(value), {precision: 0}) ?? String(value)
                },
                plotLines:
                    baseValue === null
                        ? []
                        : [
                              {
                                  value: baseValue,
                                  color: "var(--colors-secondary)",
                                  dashStyle: "Dash" as const,
                                  width: 1.5,
                                  zIndex: 4
                              }
                          ]
            },
            plotOptions: {
                column: {
                    borderRadius: 8,
                    borderWidth: 0,
                    maxPointWidth: 132,
                    pointPadding: 0.2,
                    groupPadding: 0.15,
                    dataLabels: {
                        enabled: true,
                        useHTML: true,
                        inside: false,
                        crop: false,
                        overflow: "allow" as const,
                        style: {fontSize: "1.6rem", fontWeight: "500", color: "var(--colors-secondary)", textOutline: "none"},
                        formatter: function (this: PointLabelObject) {
                            const value = this.y ?? null;

                            return value === null ? "" : `<div style="text-align:center;line-height:1.35"><b>${priceM2Format(value, {precision: 0})}</b></div>`;
                        }
                    }
                }
            },
            series: [
                {
                    type: "column" as const,
                    name: "Mediana ceny za m²",
                    data: bars.map((bar) => ({
                        y: Math.round(bar.value),
                        color: bar.color,
                        custom: {
                            kind: bar.kind,
                            label: bar.label,
                            nRows: bar.nRows,
                            periodStart: bar.periodStart,
                            periodLabel: bar.periodLabel
                        } satisfies IBarPointCustom
                    }))
                }
            ],
            tooltip: {
                // The value labels above the bars are HTML, so they live in an overlay above the
                // chart's SVG - the tooltip has to leave that container to stay on top of them.
                outside: true,
                borderColor: "transparent",
                backgroundColor: "var(--colors-white)",
                shadow: {color: "rgba(35, 35, 45, 0.24)", offsetX: 0, offsetY: 2, opacity: 0.5, width: 6},
                style: {color: "var(--colors-secondary)", fontSize: "1.2rem", zIndex: 9},
                headerFormat: "",
                pointFormatter: function (this: Point) {
                    const custom = getPointCustom(this);
                    const value = this.y ?? 0;
                    const priceLabel = `<b>${priceM2Format(value, {precision: 0})}</b>`;

                    if (!custom) {
                        return priceLabel;
                    }

                    return `${custom.label}: ${priceLabel}<br/>${custom.nRows} ${transactionsPluralize(custom.nRows)} · ${formatRcnPointPeriod({period_start: custom.periodStart, period_label: custom.periodLabel})}`;
                }
            }
        };
    }, [bars, chartHeight, showDifferenceMarks]);

    return (
        <div className={chartHolderStyle}>
            <Chart
                height={chartHeight}
                options={options}
                loader={
                    isLoading ? (
                        <CenterBox height={chartHeight}>
                            <Loader size="lg" />
                        </CenterBox>
                    ) : null
                }
            />
        </div>
    );
};

const transactionsPluralize = pluralize(["transakcja", "transakcje", "transakcji"]);

const getPointCustom = (point: Point): IBarPointCustom | undefined => point.options.custom as IBarPointCustom | undefined;

const getDifferenceLabel = (difference: number): string => `${difference > 0 ? "+" : "−"}${priceFormat(Math.abs(difference), {precision: 0})}`.toUpperCase();

const drawDifferenceMarks = (chart: ChartWithDifferenceMarks, baseValue: number | null, isEnabled: boolean) => {
    chart.differenceMarks?.destroy();

    chart.differenceMarks = undefined;

    const series = chart.series[0];

    if (!isEnabled || baseValue === null || !series) {
        return;
    }

    const group = chart.renderer.g("prices-address-difference").attr({zIndex: 6}).add();
    const baseY = series.yAxis.toPixels(baseValue, false);

    for (const point of series.points) {
        const custom = getPointCustom(point);
        const shape = point.shapeArgs;

        if (!custom || custom.kind === "okolica" || point.y === undefined || shape?.x === undefined || shape.width === undefined) {
            continue;
        }

        const difference = Math.round(point.y) - baseValue;

        if (difference === 0) {
            continue;
        }

        const x = chart.plotLeft + shape.x + shape.width + DIFFERENCE_MARK_OFFSET;
        const barY = series.yAxis.toPixels(point.y, false);
        const cap = DIFFERENCE_MARK_CAP_WIDTH / 2;

        chart.renderer
            .path([
                ["M", x, barY],
                ["L", x, baseY],
                ["M", x - cap, barY],
                ["L", x + cap, barY],
                ["M", x - cap, baseY],
                ["L", x + cap, baseY]
            ])
            .attr({"stroke-width": DIFFERENCE_MARK_STROKE})
            .css({stroke: "var(--colors-secondary)"})
            .add(group);

        // Centred on the mark, but never so high that it lands on the bar's own price label
        const labelY = Math.max((barY + baseY) / 2 + 4, Math.min(barY, baseY) + DIFFERENCE_MARK_LABEL_MIN_OFFSET);

        chart.renderer
            .text(getDifferenceLabel(difference), x + DIFFERENCE_MARK_LABEL_OFFSET, labelY)
            .css({color: "var(--colors-secondary)", fontSize: "1.24rem", fontWeight: "600", letterSpacing: "0.248px"})
            .add(group);
    }

    chart.differenceMarks = group;
};

const chartHolderStyle = css({
    width: "100%"
});
